import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Check, X, Clock } from "lucide-react";

/**
 * Phase 49e — "Last synced" pill for the Live Trades area. Polls
 * /api/upstox/sync-status every 15s and renders a compact status chip
 * (green tick + "X ago" on success, red X on failure). Clicking the
 * refresh icon nudges the poll (does not force a broker sync — that's an
 * admin-only endpoint).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const ts = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(0, Math.floor((now - ts) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch {
    return "—";
  }
};

export const UpstoxSyncPill = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/upstox/sync-status`);
      setData(r.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // Force the "X ago" text to refresh every 5s without hitting the API.
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const ok = !!data?.ok;
  const ts = data?.ts;
  const color = data == null
    ? "var(--text-muted)"
    : ok ? "var(--bull)" : "var(--bear)";
  const Icon = data == null
    ? Clock
    : ok ? Check : X;

  const summary = data && ok
    ? `${data.orders_seen ?? 0} orders · ${data.positions_seen ?? 0} positions` +
      (data.manual_sells ? ` · ${data.manual_sells} manual sell${data.manual_sells > 1 ? "s" : ""}` : "")
    : data?.message || "not synced yet";

  return (
    <div
      className="inline-flex items-center gap-2 px-2.5 py-1 border text-[10px] uppercase tracking-wider font-mono-data"
      style={{ color, borderColor: color }}
      data-testid="upstox-sync-pill"
      title={summary}
    >
      <Icon size={11} />
      <span data-testid="upstox-sync-pill-status">
        {data == null ? "checking…" : ok ? "synced" : "sync error"}
      </span>
      <span className="text-muted-3" data-testid="upstox-sync-pill-ago" data-tick={tick}>
        {ts ? timeAgo(ts) : "—"}
      </span>
      <button
        onClick={load}
        disabled={loading}
        title="Refresh sync status"
        className="ml-1 hover:opacity-70 disabled:opacity-40"
        data-testid="upstox-sync-pill-refresh"
      >
        <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
      </button>
    </div>
  );
};
